import React, { useState, useEffect } from "react";
import apiClient from "../../../../../api/apiClient";
import "./CompanyFormModal.css";

const BUSINESS_TYPES = [
    { value: "retail",       label: "Retail" },
    { value: "distributor",  label: "Distributor" },
    { value: "cultivator",   label: "Cultivator" },
];

const INDUSTRY_TYPES = [
    { value: "hemp",         label: "Hemp" },
    { value: "rec_adult_use", label: "Rec / Adult Use" },
    { value: "medical",      label: "Medical" },
];

const LICENSE_TYPES = [
    { value: "EIN",       label: "EIN" },
    { value: "USDA_hemp", label: "USDA Hemp License" },
    { value: "state",     label: "State License" },
];

const EMPTY = {
    businessName: "", businessType: "", industryType: "",
    licenseType: "", licenseNumber: "",
    businessAddress: "", city: "", state: "", zipCode: "",
    numLocations: 1, website: "", phone: "", notes: "",
};

const Req = () => <span className="cf-req">*</span>;
const Section = ({ label }) => <div className="cf-section">{label}</div>;

const CompanyFormModal = ({ company, onClose, onSaved }) => {
    const [form, setForm] = useState(company
        ? { ...EMPTY, ...company, licenseType: company.licenseType || "", numLocations: company.numLocations || 1 }
        : { ...EMPTY }
    );
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSaving(true);
        try {
            const payload = {
                ...form,
                licenseType: form.licenseType || null,
                licenseNumber: form.licenseNumber || null,
                businessAddress: form.businessAddress || null,
                city: form.city || null,
                state: form.state || null,
                zipCode: form.zipCode || null,
                website: form.website || null,
                phone: form.phone || null,
                notes: form.notes || null,
                numLocations: parseInt(form.numLocations, 10) || 1,
            };

            if (company) {
                await apiClient.put(`/admin/crm/companies/${company.id}`, payload);
            } else {
                await apiClient.post("/admin/crm/companies", payload);
            }
            onSaved();
        } catch (err) {
            setError(err.response?.data?.error || "Failed to save company");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="crm-modal-overlay" onClick={onClose}>
            <div className="crm-modal crm-modal--company-form" onClick={(e) => e.stopPropagation()}>
                <div className="crm-modal__header">
                    <span className="crm-modal__title">
                        {company ? "Edit Company" : "New Company"}
                    </span>
                    <button className="crm-modal__close" onClick={onClose}>✕</button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="crm-modal__body">
                        <Section label="Business Info" />

                        <div className="admin-form-group">
                            <label>Business Name <Req /></label>
                            <input className="admin-input" value={form.businessName} onChange={set("businessName")} required maxLength={200} />
                        </div>

                        <div className="cf-row-2">
                            <div className="admin-form-group">
                                <label>Business Type</label>
                                <select className="admin-input" value={form.businessType} onChange={set("businessType")}>
                                    <option value="">— Select —</option>
                                    {BUSINESS_TYPES.map((t) => (
                                        <option key={t.value} value={t.value}>{t.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="admin-form-group">
                                <label>Industry Type</label>
                                <select className="admin-input" value={form.industryType} onChange={set("industryType")}>
                                    <option value="">— Select —</option>
                                    {INDUSTRY_TYPES.map((t) => (
                                        <option key={t.value} value={t.value}>{t.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <Section label="License" />

                        <div className="cf-row-2">
                            <div className="admin-form-group">
                                <label>License Type</label>
                                <select className="admin-input" value={form.licenseType} onChange={set("licenseType")}>
                                    <option value="">— None —</option>
                                    {LICENSE_TYPES.map((t) => (
                                        <option key={t.value} value={t.value}>{t.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="admin-form-group">
                                <label>License Number</label>
                                <input className="admin-input" value={form.licenseNumber} onChange={set("licenseNumber")} maxLength={200} placeholder="e.g. 12-3456789" />
                            </div>
                        </div>

                        <Section label="Address" />

                        <div className="admin-form-group">
                            <label>Street Address</label>
                            <input className="admin-input" value={form.businessAddress} onChange={set("businessAddress")} maxLength={300} />
                        </div>

                        <div className="cf-row-3">
                            <div className="admin-form-group">
                                <label>City</label>
                                <input className="admin-input" value={form.city} onChange={set("city")} maxLength={100} />
                            </div>
                            <div className="admin-form-group">
                                <label>State</label>
                                <input className="admin-input" value={form.state} onChange={set("state")} maxLength={100} placeholder="e.g. CO" />
                            </div>
                            <div className="admin-form-group">
                                <label>Zip Code</label>
                                <input className="admin-input" value={form.zipCode} onChange={set("zipCode")} maxLength={20} />
                            </div>
                        </div>

                        <Section label="Details" />

                        <div className="cf-row-3">
                            <div className="admin-form-group">
                                <label>Number of Locations</label>
                                <input className="admin-input" type="number" min="1" value={form.numLocations} onChange={set("numLocations")} />
                            </div>
                            <div className="admin-form-group">
                                <label>Phone</label>
                                <input className="admin-input" value={form.phone} onChange={set("phone")} maxLength={50} />
                            </div>
                            <div className="admin-form-group">
                                <label>Website</label>
                                <input className="admin-input" value={form.website} onChange={set("website")} maxLength={500} placeholder="https://" />
                            </div>
                        </div>

                        <div className="admin-form-group">
                            <label>Notes</label>
                            <textarea className="admin-input cf-notes" value={form.notes} onChange={set("notes")} rows={3} maxLength={10000} />
                        </div>

                        {error && <div className="cf-error">{error}</div>}
                    </div>

                    <div className="crm-modal__footer">
                        <button type="button" className="admin-btn" onClick={onClose} disabled={saving}>Cancel</button>
                        <button type="submit" className="admin-btn admin-btn--primary" disabled={saving}>
                            {saving ? "Saving…" : company ? "Save Changes" : "Create Company"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CompanyFormModal;
